import { useEffect, useState } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

import { makeAuthenticatedApiCall } from '../../../apiHelper';
import { pieceDetachmentEndpoints, requestPieceDetachment } from '../../../utils/pieceDetachment';
import usePreDialogValidation from '../../../hooks/usePreDialogValidation';
import { useDepositSaleBuyer } from '../../../hooks/useApiQueries';
import theme from '../../../theme';
import { DBDeal } from '../../../types/deal';
import {
  base64ToBlob,
  documentTypes,
  documentTypesDb,
  genderTypes,
  getAdresseTypeVoie,
  getFormatFromMimetype,
  getPipedriveDealData,
  getPipedriveOrgData,
  getPipedrivePersonData,
  removeSpaces,
  resolveVoieCode,
} from '../../../utils';
import { getCommonValidationRules } from '../../../utils/formValidationRules';
import { formatBackendError } from '../../../utils/validationUtils';
import FirebaseUploadComponent from '../../CreateCotation/FirebaseUploadComponent';
import CfnPiecesWarning from './CfnPiecesWarning';
import { ErrorMessageSnackbar } from '../../ErrorMessageSnackbar';

interface GenerateAndUploadResponse {
  success: boolean;
  firebaseUrl: string;
  message: string;
  updateResult: boolean;
  rowUpdated: boolean;
}

interface CreateCessionCerfaProps {
  deal: DBDeal;
  googleDriveLink?: string;
  isDepositSale: boolean;
  isMandate: boolean;
  onRefresh: () => void;
}

const CreateCessionCerfa: React.FC<CreateCessionCerfaProps> = ({
  deal,
  googleDriveLink,
  isDepositSale,
  isMandate,
  onRefresh,
}) => {
  const [niceAddress, setNiceAddress] = useState<Record<string, string | null>>({});
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [orgName, setOrgName] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [clientType, setClientType] = useState<string>('');
  const [ownerData, setOwnerData] = useState<Record<string, string | null>>({});
  const [responseErrorCode, setResponseErrorCode] = useState(null);
  const [incompleteFieldsWarning, setIncompleteFieldsWarning] = useState<string[] | null>(null);

  const [isLoading, setIsLoading] = useState(false);

  const validation = usePreDialogValidation();

  const [downloading, setDownloading] = useState(false);
  const [downloadUrls, setDownloadUrls] = useState<string[]>([]);
  const [vin, setVin] = useState<string>(deal.vin || '');
  const [numFormule, setNumFormule] = useState<string>(deal.num_formule || '');
  const [isSavingVehicleInfo, setIsSavingVehicleInfo] = useState(false);

  const pipelineLetter = isDepositSale ? 'e' : isMandate ? 'b' : 'a';
  const { data: depositSaleBuyerData, isLoading: isDepositSaleBuyerDataLoading } = useDepositSaleBuyer(deal.id);
  const [createCessionCerfaUploadedUrls, setCreateCessionCerfaUploadedUrls] = useState<string[]>(
    pipelineLetter === 'e' ? (depositSaleBuyerData as any)?.files || [] : [],
  );

  const queryClient = useQueryClient();

  const openSnackbar = validation.openSnackbar;

  const handleDialogClose = () => {
    setIsDialogOpen(false);
  };
  const handleConfirmClick = () => {
    setIsConfirmModalOpen(true); // Ouvrir la modale de confirmation au lieu de soumettre directement
  };

  const formMethods = useForm({
    mode: 'all',
  });
  const { handleSubmit, control, formState, trigger } = formMethods;
  const { errors, isValid } = formState;

  const validationRules = getCommonValidationRules(formMethods);

  const handleButtonClick = async () => {
    const hasError = await checkVehicleOwnerInformations();

    if (!hasError) {
      setIsDialogOpen(true);
    }
  };

  const handleFinalConfirm = async () => {
    const formData = formMethods.getValues(); // Récupérer les données du formulaire actuel

    setIsConfirmModalOpen(false); // Fermer la modale de confirmation
    setIsLoading(true);
    try {
      const handleSaveResponse = await handleSave(formData);
      if (handleSaveResponse) {
        const cerfaSuccess = await handleCerfaCession(); // Appeler la fonction pour soumettre
        if (cerfaSuccess) {
          setIsDialogOpen(false);
          onRefresh();
        }
        // Si erreur, la modale reste ouverte pour que l'utilisateur puisse corriger
      }
    } catch (error: any) {
      openSnackbar(formatBackendError(error), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialiser VIN et num_formule UNE SEULE FOIS au montage du composant
  useEffect(() => {
    setVin(deal.vin || '');
    setNumFormule(deal.num_formule || '');
  }, []); // Empty dependency array = run only once on mount

  useEffect(() => {
    if (isDialogOpen) {
      trigger(); // Déclencher la validation de tous les champs

      // Réinitialiser le formulaire avec les données de ownerData si disponibles
      if (ownerData && Object.keys(ownerData).length > 0) {
        const formValues: any = {
          clientType: clientType || ownerData.client_type || '',
          lastName: clientType === 'pro' ? (ownerData as any).lastName : ownerData.last_name || '',
          firstName: clientType === 'pro' ? (ownerData as any).firstName : ownerData.first_name || '',
          email: Array.isArray(ownerData?.email)
            ? ownerData?.email?.[0]?.value || ownerData?.email?.[0] || ''
            : ownerData?.email || '',
          addressNumber: isDepositSale
            ? (ownerData as any)?.address_number
            : (ownerData.address_number ??
              (clientType === 'pro'
                ? ownerData.address_street_number
                : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_street_number'] ??
                  ownerData.address_street_number)) ??
              ''),
          addressExtension: isDepositSale
            ? (ownerData as any)?.address_extension
            : (ownerData.address_extension ??
              (clientType === 'pro'
                ? ownerData.address_subpremise
                : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_subpremise'] ??
                  ownerData.address_subpremise)) ??
              ''),
          // Normalise le code (stocké legacy che/chm/MAJUSCULE/libellé, ou dérivé)
          // vers le code du dropdown ; '' préservé si aucune source (champ vide).
          addressType: ((raw) => (raw ? resolveVoieCode(raw) : ''))(
            (depositSaleBuyerData as any)?.address_type ?? niceAddress?.typeVoie ?? ownerData.address_type,
          ),
          addressName:
            niceAddress?.nomVoie ?? (isDepositSale ? (ownerData as any)?.address_name : ownerData.address_name) ?? '',
          addressPlace: (depositSaleBuyerData as any)?.address_place ?? '',
          addressStairs: (depositSaleBuyerData as any)?.address_stairs ?? '',
          addressComp: (depositSaleBuyerData as any)?.address_comp ?? '',
          addressPostalBox: (depositSaleBuyerData as any)?.address_postalbox ?? '',
          city: isDepositSale
            ? (ownerData as any)?.city
            : (ownerData.city ??
              (clientType === 'pro'
                ? ownerData.address_locality
                : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_locality'] ?? ownerData.address_locality)) ??
              ''),
          postalCode: isDepositSale
            ? (ownerData as any)?.postal_code
            : (ownerData.postal_code ??
              (clientType === 'pro'
                ? ownerData.address_postal_code
                : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_postal_code'] ??
                  ownerData.address_postal_code)) ??
              ''),
          country: isDepositSale
            ? (ownerData as any)?.country
            : (ownerData.country ??
              (clientType === 'pro'
                ? ownerData.address_country
                : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_country'] ?? ownerData.address_country)) ??
              ''),
        };

        // Ajouter les champs spécifiques au type de client
        if (clientType === 'person') {
          formValues.dateOfBirth =
            ownerData?.date_of_birth ?? (ownerData?.['e96b6e021e72e800417bfb128337d2c0f1df47e9'] || '');
          formValues.placeOfBirth =
            ownerData?.place_of_birth ?? (ownerData?.['6a47f4d5730c056eac6437f64e812389c654a4aa'] || '');
          formValues.gender =
            (ownerData?.gender ??
              (ownerData?.['8c67afa374f8fb74710de066ea878a4f30d14c69']
                ? genderTypes[ownerData?.['8c67afa374f8fb74710de066ea878a4f30d14c69']]?.value
                : isDepositSale
                  ? ownerData.gender
                  : '')) ||
            '';
          formValues.documentId =
            ownerData?.document_id ??
            ownerData?.['6d78e71a34367d60a4dc822f4cdd78d73b376021'] ??
            (isDepositSale ? ownerData.document_id : '') ??
            '';
          formValues.documentType =
            (ownerData?.document_type && documentTypes[ownerData?.document_type]
              ? documentTypes[ownerData?.document_type]?.dbValue
              : ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b'] &&
                  documentTypes[ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b']]
                ? documentTypes[ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b']]?.dbValue
                : '') || '';
          formValues.documentIssueDate =
            ownerData?.document_issue_date ??
            ownerData?.['0ad2c6ddd477b9b29b4d18858b36f0aee3173b1c'] ??
            (isDepositSale ? ownerData.document_issue_date : '') ??
            '';
        }

        if (clientType === 'pro') {
          formValues.siret =
            (ownerData?.siret ??
              (isMandate
                ? ownerData?.siret
                : (ownerData['cd6bc84ef4b1392fb08f9fdb9071cccb2c96ce1f'] ?? (isDepositSale ? ownerData.siret : '')))) ||
            '';
          formValues.companyName =
            (ownerData?.company_name ??
              (isMandate
                ? (ownerData as any)?.companyName
                : (ownerData['d7f00bd23217519c4d134d312d1206bc4a7021c6'] ?? orgName))) ||
            '';
        }

        formMethods.reset(formValues);
      }
    }
  }, [
    isDialogOpen,
    trigger,
    ownerData,
    clientType,
    niceAddress,
    isDepositSale,
    isMandate,
    depositSaleBuyerData,
    orgName,
    formMethods,
  ]);

  useEffect(() => {
    setCreateCessionCerfaUploadedUrls((depositSaleBuyerData as any)?.files || []);
  }, [deal, depositSaleBuyerData, pipelineLetter]);

  const checkVehicleOwnerInformations = async () => {
    try {
      let _ownerData: any;
      let _niceAddress: any;
      let _clientType: string = 'person';
      let _orgName: string = '';
      // Reset du bandeau d'avertissement à chaque appel — il sera ré-armé si Pipedrive est incomplet.
      setIncompleteFieldsWarning(null);

      if (isMandate) {
        _clientType = 'pro';

        // 1. Essayer d'abord owner_pipeline_b : données validées avec SIRET complet (14 chiffres)
        const localOwnerData: Record<string, any> | null = await (async () => {
          try {
            const response = await makeAuthenticatedApiCall('get', `/api/owners/b/${deal.id}`);
            return (response.data as Record<string, any>) || null;
          } catch {
            return null;
          }
        })();

        // 2. Récupérer depuis Pipedrive : orgId/personId (requis) + email du contact
        const pipedriveDealData = await getPipedriveDealData(deal.id);
        _orgName = pipedriveDealData?.org_name;
        const orgId = pipedriveDealData?.org_id?.value;
        const personId = pipedriveDealData?.person_id?.value;

        if (!orgId) {
          throw new Error(
            `❌ ORGANISATION MANQUANTE (Pipeline MANDATE)\n\n` +
              `Deal ID: ${deal.id}\n\n` +
              `Le champ "Organisation" (org_id) est vide dans Pipedrive.\n\n` +
              `➡️ Action requise: Associer une organisation au deal dans Pipedrive`,
          );
        }

        if (!personId) {
          throw new Error(
            `❌ PERSONNE MANQUANTE (Pipeline MANDATE)\n\n` +
              `Deal ID: ${deal.id}\n\n` +
              `Le champ "Personne" (person_id) est vide dans Pipedrive.\n\n` +
              `➡️ Action requise: Associer une personne de contact au deal dans Pipedrive`,
          );
        }

        const agencyPersonData = await getPipedrivePersonData(personId);
        const agencyEmail = Array.isArray(agencyPersonData?.email)
          ? agencyPersonData.email[0]?.value || agencyPersonData.email[0] || ''
          : agencyPersonData?.email || '';

        if (localOwnerData) {
          // Chemin principal : données BDD (owner_pipeline_b)
          _clientType = localOwnerData.client_type ?? 'pro';
          _orgName = localOwnerData.company_name || _orgName;
          _niceAddress = {
            typeVoie: localOwnerData.address_type || '',
            nomVoie: localOwnerData.address_name || '',
          };
          _ownerData = {
            client_type: _clientType,
            // Alias camelCase pour les champs nom : le useEffect les lit en camelCase quand clientType='pro'
            firstName: localOwnerData.first_name || '',
            lastName: localOwnerData.last_name || '',
            first_name: localOwnerData.first_name || '',
            last_name: localOwnerData.last_name || '',
            email: agencyEmail,
            address_number: localOwnerData.address_number || '',
            address_extension: localOwnerData.address_extension || null,
            address_type: localOwnerData.address_type || '',
            address_name: localOwnerData.address_name || '',
            city: localOwnerData.city || '',
            postal_code: localOwnerData.postal_code || '',
            country: localOwnerData.country || '',
            siret: localOwnerData.siret || null,
            company_name: localOwnerData.company_name || null,
          };
        } else {
          // Fallback Pipedrive si owner_pipeline_b absent
          const _purchaserData = await getPipedriveOrgData(orgId);

          // Fallback sur le texte brut de l'adresse quand les sous-champs parsés (address_route /
          // address_street_number) sont vides — cas fréquent quand l'utilisateur n'a pas validé
          // via l'autocomplétion Google Maps dans Pipedrive.
          const rawAddressText = (_purchaserData?.address as string) || '';
          const rawFirstSegment = rawAddressText.split(',')[0]?.trim() || '';
          const rawNumberMatch = rawFirstSegment.match(/^(\d+\s*(?:bis|ter|[A-Za-z])?)\s+(.+)$/);
          const fallbackStreetNumber = rawNumberMatch?.[1]?.trim() ?? '';
          const fallbackRoute = rawNumberMatch?.[2]?.trim() ?? rawFirstSegment;

          const effectiveRoute = _purchaserData?.address_route || fallbackRoute || '';
          const effectiveStreetNumber = _purchaserData?.address_street_number || fallbackStreetNumber || '';

          const missingMandateFields = [];
          if (!_purchaserData?.address_postal_code) {
            missingMandateFields.push("Code postal de l'organisation (address_postal_code)");
          }
          if (!_purchaserData?.address_locality) {
            missingMandateFields.push("Ville de l'organisation (address_locality)");
          }
          if (!_purchaserData?.address_country) {
            missingMandateFields.push("Pays de l'organisation (address_country)");
          }
          if (!effectiveRoute) {
            missingMandateFields.push("Rue de l'organisation (address_route ou address brut)");
          }
          if (!agencyPersonData?.first_name) {
            missingMandateFields.push('Prénom du contact (first_name)');
          }
          if (!agencyPersonData?.last_name) {
            missingMandateFields.push('Nom du contact (last_name)');
          }

          if (missingMandateFields.length > 0) {
            console.warn(
              '⚠️ Champs Pipedrive incomplets pour MANDATE — ouverture de la modale en saisie manuelle:',
              missingMandateFields,
            );
            setIncompleteFieldsWarning(missingMandateFields);
          } else {
            setIncompleteFieldsWarning(null);
          }

          _niceAddress = getAdresseTypeVoie(effectiveRoute);

          _ownerData = {
            client_type: 'pro',
            firstName: agencyPersonData?.first_name || '',
            lastName: agencyPersonData?.last_name || '',
            first_name: agencyPersonData?.first_name || '',
            last_name: agencyPersonData?.last_name || '',
            email: agencyEmail,
            // snake_case pour les champs adresse (lus par le useEffect)
            address_number: effectiveStreetNumber,
            address_extension: _purchaserData?.address_subpremise || null,
            address_street_number: effectiveStreetNumber,
            address_subpremise: _purchaserData?.address_subpremise || null,
            city: _purchaserData?.address_locality || '',
            address_locality: _purchaserData?.address_locality || '',
            postal_code: _purchaserData?.address_postal_code || '',
            address_postal_code: _purchaserData?.address_postal_code || '',
            country: _purchaserData?.address_country || '',
            address_country: _purchaserData?.address_country || '',
            siret: _purchaserData?.['cd6bc84ef4b1392fb08f9fdb9071cccb2c96ce1f'],
            company_name: _purchaserData?.['d7f00bd23217519c4d134d312d1206bc4a7021c6'] ?? pipedriveDealData?.org_name,
            companyName: _purchaserData?.['d7f00bd23217519c4d134d312d1206bc4a7021c6'] ?? pipedriveDealData?.org_name,
          };
        }
      } else if (isDepositSale) {
        _ownerData = depositSaleBuyerData as any;
        _clientType = (depositSaleBuyerData as any).client_type ?? 'person';
        _orgName = (depositSaleBuyerData as any).company_name;
      } else {
        // Essayer d'abord de récupérer les données depuis la base de données locale (où elles sont sauvegardées)
        const localOwnerData = await (async () => {
          try {
            const response = await makeAuthenticatedApiCall('get', `/api/owners/${pipelineLetter}/${deal.id}`);
            if (response.data) {
              return response.data;
            }
            return null;
          } catch (error) {
            return null;
          }
        })();

        // Si on a des données locales, les utiliser
        if (localOwnerData) {
          // Mapper les données de la base de données (snake_case) vers le format attendu
          _ownerData = {
            client_type: localOwnerData.client_type ?? 'person',
            first_name: localOwnerData.first_name || '',
            last_name: localOwnerData.last_name || '',
            date_of_birth: localOwnerData.date_of_birth || null,
            place_of_birth: localOwnerData.place_of_birth || null,
            address_number: localOwnerData.address_number || '',
            address_extension: localOwnerData.address_extension || null,
            address_type: localOwnerData.address_type || '',
            address_name: localOwnerData.address_name || '',
            city: localOwnerData.city || '',
            postal_code: localOwnerData.postal_code || '',
            country: localOwnerData.country || '',
            gender: localOwnerData.gender || null,
            document_id: localOwnerData.document_id || null,
            document_type: localOwnerData.document_type || null,
            document_issue_date: localOwnerData.document_issue_date || null,
            company_name: localOwnerData.company_name || null,
            siret: localOwnerData.siret || null,
            // Pour compatibilité avec le code existant qui utilise les clés Pipedrive
            cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_street_number: localOwnerData.address_number || '',
            cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_subpremise: localOwnerData.address_extension || null,
            cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_postal_code: localOwnerData.postal_code || '',
            cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_locality: localOwnerData.city || '',
            cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_country: localOwnerData.country || '',
            address_street_number: localOwnerData.address_number || '',
            address_subpremise: localOwnerData.address_extension || null,
            address_postal_code: localOwnerData.postal_code || '',
            address_locality: localOwnerData.city || '',
            address_country: localOwnerData.country || '',
          };
          _clientType = localOwnerData.client_type ?? 'person';
          _orgName = localOwnerData.company_name || '';

          // Construire _niceAddress depuis les données locales
          const addressType = localOwnerData.address_type || '';
          const addressName = localOwnerData.address_name || '';
          _niceAddress = { typeVoie: addressType, nomVoie: addressName };
        } else {
          // Sinon, récupérer depuis Pipedrive (comportement original)
          const pipedriveDealData = await getPipedriveDealData(deal.id);

          _orgName = pipedriveDealData?.org_name;
          const personId = pipedriveDealData['bb9522550edf45372ab2587bd49adbbd09f98a7b']?.value;
          const orgId = pipedriveDealData['a1af3a159b8c9f88ea655ae2c846ecef39136ce9']?.value;

          if (!personId && !orgId) {
            const missingFields = [];
            if (!pipedriveDealData['bb9522550edf45372ab2587bd49adbbd09f98a7b']) {
              missingFields.push('Champ Pipedrive "Acheteur Particulier" (bb9522550edf45372ab2587bd49adbbd09f98a7b)');
            }
            if (!pipedriveDealData['a1af3a159b8c9f88ea655ae2c846ecef39136ce9']) {
              missingFields.push('Champ Pipedrive "Acheteur Pro" (a1af3a159b8c9f88ea655ae2c846ecef39136ce9)');
            }

            console.error('❌ Champs manquants dans Pipedrive:', missingFields);
            console.error('📋 Données complètes du deal:', JSON.stringify(pipedriveDealData, null, 2));

            throw new Error(
              `❌ TITULAIRE MANQUANT - Aucun acheteur trouvé dans Pipedrive.\n\n` +
                `Champs manquants:\n` +
                `${missingFields.map((f, i) => `  ${i + 1}. ${f}`).join('\n')}\n\n` +
                `➡️ Action requise: Remplir "Acheteur Particulier" OU "Acheteur Pro" dans Pipedrive pour le deal #${deal.id}`,
            );
          }

          _ownerData = personId ? await getPipedrivePersonData(personId) : await getPipedriveOrgData(orgId);
          _clientType = orgId ? 'pro' : 'person';

          // Récupération des champs d'adresse depuis Pipedrive
          const postalCode =
            _ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_postal_code'] || _ownerData['address_postal_code'];
          const city =
            _ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_locality'] || _ownerData['address_locality'];
          const country =
            _ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_country'] || _ownerData['address_country'];
          const streetNumber =
            _ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_street_number'] || _ownerData['address_street_number'];
          const initialAddressRoute =
            _ownerData['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_route'] || _ownerData['address_route'];

          // Détection du cas lieu-dit : route vide mais adresse complète présente avec code postal/ville/pays remplis
          const fullAddress =
            _ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839'] || _ownerData?.['postal_address'] || '';
          const isLieuDitCase = !initialAddressRoute && fullAddress && postalCode && city && country;

          // Déclaration de addressRoute qui sera utilisé plus tard
          let addressRoute = initialAddressRoute;

          if (isLieuDitCase) {
            // Extraction du lieu-dit depuis l'adresse complète (tout ce qui est avant la première virgule)
            const lieuDitMatch = fullAddress.match(/^([^,]+)/);
            if (lieuDitMatch) {
              addressRoute = lieuDitMatch[1].trim();
              // Configuration pour lieu-dit : typeVoie = 'ldit' et nomVoie = lieu-dit
              _niceAddress = { typeVoie: 'ldit', nomVoie: addressRoute };
            }
          } else {
            // Cas normal : traitement standard avec getAdresseTypeVoie
            const rawAddressRoute = initialAddressRoute || '';
            _niceAddress = getAdresseTypeVoie(rawAddressRoute);
          }

          // Vérification détaillée des champs obligatoires
          const missingOwnerFields = [];

          if (!postalCode) {
            console.warn("⚠️ Code postal manquant pour le titulaire — la modale s'ouvrira sans ce champ pré-rempli");
          }
          if (!city) {
            missingOwnerFields.push('Ville (cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_locality ou address_locality)');
          }
          if (!country) {
            missingOwnerFields.push('Pays (cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_country ou address_country)');
          }
          // Ne pas considérer la route comme manquante si c'est un cas de lieu-dit
          if (!addressRoute && !isLieuDitCase) {
            missingOwnerFields.push('Rue (cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_route ou address_route)');
          }

          if (_clientType === 'person') {
            if (!_ownerData.first_name) {
              missingOwnerFields.push('Prénom (first_name)');
            }
            if (!_ownerData.last_name) {
              missingOwnerFields.push('Nom (last_name)');
            }
          }

          if (_clientType === 'pro') {
            const companyName = _ownerData?.['d7f00bd23217519c4d134d312d1206bc4a7021c6'] || _ownerData?.name;
            if (!companyName) {
              missingOwnerFields.push("Nom de l'entreprise (d7f00bd23217519c4d134d312d1206bc4a7021c6 ou name)");
            }
          }

          if (missingOwnerFields.length > 0) {
            console.warn(
              '⚠️ Champs Pipedrive incomplets pour le titulaire — ouverture de la modale en saisie manuelle:',
              missingOwnerFields,
            );
            setIncompleteFieldsWarning(missingOwnerFields);
          }

          if (!_ownerData) {
            throw new Error('❌ Les données du titulaire sont complètement absentes de Pipedrive');
          }
        }
      }

      setClientType(_clientType);
      setOrgName(_orgName);
      setNiceAddress(_niceAddress);
      setOwnerData(_ownerData);
      validation.resetValidation();
      return false;
    } catch (error: any) {
      console.error('❌ DEBUG - Error in checkVehicleOwnerInformations:', error);
      validation.setTooltipError(error.message);
      validation.setDisabledButton(true);
      openSnackbar(error.message, 'error');
      return true;
    }
  };

  const handleSave = async (formData: any /* FormDataPerson | FormDataPro */) => {
    const submissionData = {
      ...formData,
      fromCessionCerfa: true,
      documentType: documentTypesDb[formData.documentType] || formData.documentType, // Fallback sur la valeur originale au cas où
    };

    try {
      if (isDepositSale) {
        const responseSave = await makeAuthenticatedApiCall(
          'post',
          `/api/deals/${deal.id}/confirm-deposit-sale`,
          submissionData,
        );
      } else {
        const responseSave = await makeAuthenticatedApiCall(
          'post',
          `/api/owners/${pipelineLetter}/${deal.id}`,
          submissionData,
        );
      }
      // Ne pas afficher de snackbar ici, handleSaveVehicleInfo s'en chargera
      return true;
    } catch (error: any) {
      console.error('❌ [CreateCessionCerfa] Erreur dans handleSave:', error);
      console.error('❌ [CreateCessionCerfa] Error response:', error.response?.data);
      // Ne pas afficher de snackbar ici non plus, handleSaveVehicleInfo s'en chargera
      throw error; // Re-throw pour que handleSaveVehicleInfo puisse gérer l'erreur
    } finally {
      setResponseErrorCode(null);
    }
  };

  const handleSaveVehicleInfo = async () => {
    setIsSavingVehicleInfo(true);

    // Récupérer toutes les données du formulaire
    const formData = formMethods.getValues();

    try {
      // 1. Sauvegarder TOUTES les données du formulaire (propriétaire, etc.)
      await handleSave(formData);

      // 2. Mise à jour VIN et num_formule dans car_deals
      const response1 = await makeAuthenticatedApiCall('patch', `/api/deals/${deal.id}/vehicle-info`, {
        vin: vin || null,
        num_formule: numFormule || null,
      });

      // 3. Mise à jour VIN et num_formule dans Pipedrive
      const response2 = await makeAuthenticatedApiCall('patch', `/api/pipedrive/deals/${deal.id}/vehicle-info`, {
        vin: vin || null,
        num_formule: numFormule || null,
      });

      openSnackbar('✅ Informations du véhicule et du propriétaire mises à jour avec succès', 'success');

      // Mettre à jour les valeurs locales de VIN et num_formule immédiatement
      setVin(vin || '');
      setNumFormule(numFormule || '');

      // Invalider ET refetch les queries pour une actualisation complète
      queryClient.invalidateQueries({ queryKey: ['useArbitratorDeals'] });
      queryClient.invalidateQueries({ queryKey: ['useDatabaseDeals'] });

      await queryClient.refetchQueries({ queryKey: ['useArbitratorDeals'] });
      await queryClient.refetchQueries({ queryKey: ['useDatabaseDeals'] });

      // Rafraîchir les données si onRefresh est fourni
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde:', error);
      openSnackbar(error.response?.data?.error || error.message || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setIsSavingVehicleInfo(false);
    }
  };

  const handleCerfaCession = async (): Promise<boolean> => {
    try {
      const updatedDeal = { ...deal, isDepositSale };

      // Use pipeline B endpoint for mandate, pipeline A for others
      const endpoint = isMandate
        ? `/api/deals/${deal.id}/pipeline-b/generate-and-upload-cession-cerfa`
        : `/api/deals/${deal.id}/generate-and-upload-cerfa-pdf`;

      const generateAndUploadResponse = await makeAuthenticatedApiCall('post', endpoint, updatedDeal);

      const responseData = generateAndUploadResponse.data as GenerateAndUploadResponse;
      if (responseData?.success && responseData.firebaseUrl) {
        window.open(responseData.firebaseUrl, '_blank');

        openSnackbar('Certificat de cession généré et uploadé avec succès !', 'success');

        // Trigger event to refresh SendProcurementRequestButton
        window.dispatchEvent(new CustomEvent('cerfaPdfUploaded', { detail: { dealId: deal.id } }));

        return true;
      } else {
        openSnackbar('Erreur lors de la génération et upload du PDF', 'warning');
        return false;
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.details ||
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Erreur inconnue';
      openSnackbar(errorMessage, 'error');
      return false;
    }
  };

  const handleDownloadPhotos = async () => {
    setDownloading(true);
    const zip = new JSZip();
    const pipedriveDealId = deal.id;
    const folder = zip.folder(`Skipcar-DC-${pipedriveDealId}`);

    // Parse the deal.AllFiles string into an array of URLs
    const urls = (depositSaleBuyerData as any)?.files || [];
    setDownloadUrls(urls);

    const promises = urls.map(async (url: string, index: number) => {
      try {
        const response = await axios.get(url, { responseType: 'arraybuffer' });
        const blob = new Blob([response.data], { type: String(response.headers['content-type'] ?? '') });
        const fileName = `Skipcar-DC-${pipedriveDealId}-${index}.${getFormatFromMimetype(String(response.headers['content-type'] ?? ''))}`;
        folder?.file(fileName, blob);
      } catch (error) {
        console.error('Error handling photo:', error);
      }
    });

    await Promise.all(promises);

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `Skipcar-DC-${pipedriveDealId}.zip`);
    } catch (error) {
      console.error('Error generating ZIP:', error);
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadComplete = (url: string) => {
    setCreateCessionCerfaUploadedUrls((prevUrls) => [...(prevUrls || []), url]);
  };

  // Pipeline E : la liste vient de `deposit_sale_buyer.files` (périmètre
  // CFN). La référence en base est retirée AVANT l'objet Storage.
  const handleBeforeDelete = async (url: string) => {
    if (pipelineLetter !== 'e') return;
    await requestPieceDetachment(pieceDetachmentEndpoints.depositSaleBuyer(deal.id), url);
  };

  const handleDeleteComplete = (url: string) => {
    setCreateCessionCerfaUploadedUrls((prevUrls) => (prevUrls || []).filter((existingUrl) => existingUrl !== url));
    if (pipelineLetter === 'e') {
      void queryClient.invalidateQueries({ queryKey: ['useDepositSaleBuyer', deal.id] });
    }
  };

  return (
    <>
      <Tooltip
        placement="top"
        title={validation.tooltipError ? validation.tooltipError : !deal?.num_formule ? 'Numéro de formule absent' : ''}
      >
        <span>
          <Button
            className="dealStageActionsButton"
            variant="contained"
            onClick={handleButtonClick}
            disabled={!deal?.num_formule || validation.disabledButton}
          >
            {isLoading ? (
              <CircularProgress size={24} style={{ color: 'white' }} />
            ) : (
              '✍️ Certificat de Cession à signer'
            )}
          </Button>
        </span>
      </Tooltip>
      {validation.tooltipError && (
        <Typography
          color="error"
          sx={{
            fontSize: 12,
            marginTop: 1,
            textAlign: 'center',
          }}
        >
          {validation.tooltipError}
        </Typography>
      )}

      <Dialog open={isDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="lg">
        {isDepositSaleBuyerDataLoading ? (
          <CircularProgress />
        ) : (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(handleSave)}>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={12} sx={{ textAlign: 'center' }}>
                    <Typography variant="body1" textAlign="center">
                      {isDepositSale
                        ? "Veuillez vérifier les informations provenants de Skipcar dans l'étape E1"
                        : 'Veuillez vérifier les informations provenants de Pipedrive.'}
                    </Typography>
                    <Typography variant="body2" textAlign="center">
                      {isDepositSale
                        ? "Nous les utilisons pour faire l'envoi au SIV."
                        : "Nous les sauvegardons dans notre base de données et les utilisons pour faire l'envoi au SIV."}
                    </Typography>
                  </Grid>
                  {incompleteFieldsWarning && incompleteFieldsWarning.length > 0 && (
                    <Grid item xs={12}>
                      <Alert severity="warning" sx={{ whiteSpace: 'pre-line' }}>
                        <strong>Données Pipedrive incomplètes :</strong>
                        {'\n'}
                        {incompleteFieldsWarning.map((f) => `• ${f}`).join('\n')}
                        {'\n\n'}
                        Complétez les champs manquants ci-dessous — vos saisies seront enregistrées et primeront sur
                        Pipedrive pour les générations futures.
                      </Alert>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <TextField
                      className="inputElement"
                      margin="dense"
                      id="vin"
                      label="VIN"
                      type="text"
                      fullWidth
                      value={vin}
                      onChange={(e) => setVin(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      className="inputElement"
                      margin="dense"
                      id="numFormule"
                      label="Numéro de formule"
                      type="text"
                      fullWidth
                      value={numFormule}
                      onChange={(e) => setNumFormule(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="clientType"
                      control={control}
                      defaultValue={clientType ?? ''}
                      rules={{ required: 'Ce champ est requis' }}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl component="fieldset" margin="dense" error={!!error} fullWidth>
                          <FormLabel component="legend" sx={{ fontSize: '0.75rem' }}>
                            Type de client
                          </FormLabel>
                          <RadioGroup
                            {...field}
                            row
                            id="clientType"
                            value={field.value || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              field.onChange(newValue);
                              setClientType(newValue);
                            }}
                          >
                            <FormControlLabel value="person" control={<Radio size="small" />} label="Particulier" />
                            <FormControlLabel value="pro" control={<Radio size="small" />} label="Professionnel" />
                          </RadioGroup>
                          {error && <FormHelperText>{error.message}</FormHelperText>}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={validationRules.lastName}
                      defaultValue={clientType === 'pro' ? ownerData?.lastName : (ownerData?.last_name ?? '')}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          id="lastName"
                          error={!!errors.lastName}
                          className="inputElement"
                          margin="dense"
                          label="Nom"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={validationRules.firstName}
                      defaultValue={clientType === 'pro' ? ownerData?.firstName : (ownerData?.first_name ?? '')}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          error={!!errors.firstName}
                          className="inputElement"
                          margin="dense"
                          id="firstName"
                          label="Prénom"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="email"
                      control={control}
                      rules={{ required: 'Ce champ est requis' }}
                      defaultValue={
                        Array.isArray(ownerData?.email)
                          ? ownerData?.email?.[0]?.value || ownerData?.email?.[0] || ''
                          : ownerData?.email || ''
                      }
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ''}
                          className="inputElement"
                          margin="dense"
                          id="email"
                          label="Email"
                          type="email"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="addressNumber"
                      control={control}
                      rules={validationRules.addressNumber}
                      defaultValue={
                        isDepositSale
                          ? (ownerData as any)?.address_number
                          : clientType === 'pro'
                            ? ownerData?.address_street_number
                            : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_street_number'] ??
                              ownerData?.address_street_number)
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          error={!!errors.addressNumber}
                          className="inputElement"
                          margin="dense"
                          id="addressNumber"
                          label="Numéro d'adresse"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name="addressExtension"
                      control={control}
                      defaultValue={
                        isDepositSale
                          ? (ownerData as any)?.address_extension
                          : clientType === 'pro'
                            ? ownerData?.address_subpremise
                            : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_subpremise'] ??
                              ownerData?.address_subpremise)
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="inputElement"
                          margin="dense"
                          id="addressExtension"
                          label="Extension d'adresse"
                          type="text"
                          fullWidth
                          inputProps={{ maxLength: 10 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Controller
                      name={'addressType'}
                      control={control}
                      defaultValue={
                        (depositSaleBuyerData as any)?.address_type ?? niceAddress?.typeVoie?.toLowerCase() ?? ''
                      }
                      rules={validationRules.addressType}
                      render={({ field, fieldState: { error } }) => (
                        <FormControl fullWidth error={!!error}>
                          <InputLabel className="inputElement" id={`${field.name}-label`}>
                            Type d'adresse
                          </InputLabel>
                          <Select
                            className="inputElement"
                            sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                            {...field}
                            labelId={`${field.name}-label`}
                            id={field.name}
                            label="Type d'adresse"
                            value={field.value ? field.value.toLowerCase() : ''}
                          >
                            <MenuItem value="rue">Rue</MenuItem>
                            <MenuItem value="all">Allée</MenuItem>
                            <MenuItem value="avn">Avenue</MenuItem>
                            <MenuItem value="blvd">Boulevard</MenuItem>
                            <MenuItem value="chau">Chaussée</MenuItem>
                            <MenuItem value="che">Chemin</MenuItem>
                            <MenuItem value="cour">Cour</MenuItem>
                            <MenuItem value="fbg">Faubourg</MenuItem>
                            <MenuItem value="fort">Fort</MenuItem>
                            <MenuItem value="imp">Impasse</MenuItem>
                            <MenuItem value="ldit">Lieu-Dit</MenuItem>
                            <MenuItem value="parc">Parc</MenuItem>
                            <MenuItem value="pass">Passage</MenuItem>
                            <MenuItem value="plc">Place</MenuItem>
                            <MenuItem value="quai">Quai</MenuItem>
                            <MenuItem value="rte">Route</MenuItem>
                            <MenuItem value="sent">Sentier</MenuItem>
                            <MenuItem value="squa">Square</MenuItem>
                            <MenuItem value="sans">Inconnu</MenuItem>
                          </Select>
                          {error && <FormHelperText>{error.message}</FormHelperText>}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="addressName"
                      control={control}
                      rules={validationRules.addressName}
                      defaultValue={niceAddress?.nomVoie ?? (isDepositSale ? (ownerData as any)?.address_name : '')}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="inputElement"
                          error={!!errors.addressName}
                          margin="dense"
                          id="addressName"
                          label="Nom de la voie (Rue, Allée, ou Avenue, ne doivent pas apparaitre ici)"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={'addressPlace'}
                      control={control}
                      rules={{}}
                      defaultValue={(depositSaleBuyerData as any)?.address_place ?? ''}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ''}
                          className="inputElement"
                          margin="dense"
                          id={'addressPlace'}
                          label="Lieu-dit"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={'addressStairs'}
                      control={control}
                      rules={{}}
                      defaultValue={(depositSaleBuyerData as any)?.address_stairs ?? ''}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ''}
                          className="inputElement"
                          margin="dense"
                          id={'addressStairs'}
                          label="Étage/Escalier/Appartement"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={'addressComp'}
                      control={control}
                      rules={{}}
                      defaultValue={(depositSaleBuyerData as any)?.address_comp ?? ''}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ''}
                          className="inputElement"
                          margin="dense"
                          id={'addressComp'}
                          label="Complément d'adresse"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={'addressPostalBox'}
                      control={control}
                      rules={{}}
                      defaultValue={(depositSaleBuyerData as any)?.address_postalbox ?? ''}
                      render={({ field, fieldState: { error } }) => (
                        <TextField
                          {...field}
                          error={!!error}
                          helperText={error ? error.message : ''}
                          className="inputElement"
                          margin="dense"
                          id={'addressPostalBox'}
                          label="Boîte postale"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="city"
                      control={control}
                      rules={validationRules.city}
                      defaultValue={
                        isDepositSale
                          ? (ownerData as any)?.city
                          : clientType === 'pro'
                            ? ownerData?.address_locality
                            : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_locality'] ??
                              ownerData?.address_locality)
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          error={!!errors.city}
                          className="inputElement"
                          margin="dense"
                          id="city"
                          label="Ville"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="postalCode"
                      control={control}
                      rules={validationRules.postalCode}
                      defaultValue={
                        isDepositSale
                          ? (ownerData as any)?.postal_code
                          : clientType === 'pro'
                            ? ownerData?.address_postal_code
                            : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_postal_code'] ??
                              ownerData?.address_postal_code)
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="inputElement"
                          error={!!errors.postalCode}
                          margin="dense"
                          id="postalCode"
                          label="Code postal"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name="country"
                      control={control}
                      rules={validationRules.country}
                      defaultValue={
                        isDepositSale
                          ? (ownerData as any)?.country
                          : clientType === 'pro'
                            ? ownerData?.address_country
                            : (ownerData?.['cdbbea0bbb21d6bb4fa5ed9ffa679c2a817d5839_country'] ??
                              ownerData?.address_country)
                      }
                      render={({ field }) => (
                        <TextField
                          {...field}
                          className="inputElement"
                          error={!!errors.country}
                          margin="dense"
                          id="country"
                          label="Pays"
                          type="text"
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                  {clientType === 'person' && (
                    <>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="dateOfBirth"
                          rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                          control={control}
                          defaultValue={
                            ownerData?.['e96b6e021e72e800417bfb128337d2c0f1df47e9']
                              ? ownerData?.['e96b6e021e72e800417bfb128337d2c0f1df47e9']
                              : (ownerData?.date_of_birth ?? '')
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              id="dateOfBirth"
                              error={!!errors.dateOfBirth}
                              sx={{ marginTop: '8px' }}
                              className="inputElement"
                              label="Date de naissance"
                              type="date"
                              fullWidth
                              margin="normal"
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="placeOfBirth"
                          control={control}
                          rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                          defaultValue={
                            ownerData?.['6a47f4d5730c056eac6437f64e812389c654a4aa']
                              ? ownerData?.['6a47f4d5730c056eac6437f64e812389c654a4aa']
                              : (ownerData?.place_of_birth ?? '')
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              error={!!errors.placeOfBirth}
                              className="inputElement"
                              margin="dense"
                              id="placeOfBirth"
                              label="Lieu de naissance"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="gender"
                          control={control}
                          rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                          defaultValue={(() => {
                            const value = ownerData?.['8c67afa374f8fb74710de066ea878a4f30d14c69']
                              ? genderTypes[ownerData?.['8c67afa374f8fb74710de066ea878a4f30d14c69']].value
                              : isDepositSale
                                ? ownerData.gender
                                : '';
                            return value;
                          })()}
                          render={({ field }) => (
                            <Select
                              sx={{ minHeight: 'inherit', height: '56px', marginTop: '8px' }}
                              {...field}
                              error={!!errors.gender}
                              margin="dense"
                              fullWidth
                              className="inputElement"
                            >
                              <MenuItem value="male">Masculin</MenuItem>
                              <MenuItem value="female">Féminin</MenuItem>
                            </Select>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="documentId"
                          rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                          control={control}
                          defaultValue={
                            ownerData?.['6d78e71a34367d60a4dc822f4cdd78d73b376021']
                              ? (ownerData?.['6d78e71a34367d60a4dc822f4cdd78d73b376021'] ?? '')
                              : isDepositSale
                                ? ownerData.document_id
                                : ''
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              margin="dense"
                              error={!!errors.documentId}
                              className="inputElement"
                              id="documentId"
                              label="ID du document"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="documentType"
                          rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                          control={control}
                          defaultValue={
                            ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b']
                              ? documentTypes[ownerData?.['34387af45e0ae48b44f61195ee2f755a00f2280b']]?.dbValue || '' // Utiliser dbValue comme valeur par défaut
                              : isDepositSale
                                ? ownerData.document_type
                                : ''
                          }
                          render={({ field }) => (
                            <Select
                              {...field}
                              sx={{ minHeight: 'inherit', height: '56px', marginTop: '0' }}
                              error={!!errors.documentType}
                              fullWidth
                              className="inputElement"
                            >
                              <MenuItem value="id_card">Carte d'identité</MenuItem>
                              <MenuItem value="passport">Passeport</MenuItem>
                              <MenuItem value="driving_licence">Permis de conduire</MenuItem>
                              <MenuItem value="residence_permit">Titre de séjour</MenuItem>
                            </Select>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Controller
                          name="documentIssueDate"
                          rules={clientType === 'person' ? { required: 'Ce champ est requis' } : {}}
                          control={control}
                          defaultValue={
                            ownerData?.['0ad2c6ddd477b9b29b4d18858b36f0aee3173b1c']
                              ? ownerData?.['0ad2c6ddd477b9b29b4d18858b36f0aee3173b1c']
                              : isDepositSale
                                ? ownerData.document_issue_date
                                : ''
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              sx={{ marginTop: '0px' }}
                              margin="dense"
                              error={!!errors.documentIssueDate}
                              className="inputElement"
                              id="documentIssueDate"
                              label="Date de délivrance du document"
                              type="date"
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
                  {clientType === 'pro' && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="siret"
                          control={control}
                          rules={
                            clientType === 'pro'
                              ? {
                                  required: 'Ce champ est requis',
                                  minLength: {
                                    value: 14,
                                    message: 'Le SIRET doit contenir exactement 14 caractères',
                                  },
                                  maxLength: {
                                    value: 14,
                                    message: 'Le SIRET doit contenir exactement 14 caractères',
                                  },
                                }
                              : {
                                  minLength: {
                                    value: 14,
                                    message: 'Le SIRET doit contenir exactement 14 caractères',
                                  },
                                  maxLength: {
                                    value: 14,
                                    message: 'Le SIRET doit contenir exactement 14 caractères',
                                  },
                                }
                          }
                          defaultValue={
                            isMandate
                              ? ownerData?.siret
                              : (ownerData['cd6bc84ef4b1392fb08f9fdb9071cccb2c96ce1f'] ?? isDepositSale)
                                ? ownerData.siret
                                : ''
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              className="inputElement"
                              error={!!errors.siret}
                              helperText={errors.siret ? (errors.siret.message as string) : ''}
                              margin="dense"
                              id="siret"
                              label="SIRET"
                              type="text"
                              fullWidth
                              inputProps={{ maxLength: 14 }} // Limite le nombre de caractères que l'utilisateur peut taper
                              onChange={(e) => field.onChange(removeSpaces(e.target.value))} // Enlève les espaces
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="companyName"
                          control={control}
                          rules={clientType === 'pro' ? { required: 'Ce champ est requis' } : {}}
                          defaultValue={
                            isMandate
                              ? ownerData?.companyName
                              : (ownerData['d7f00bd23217519c4d134d312d1206bc4a7021c6'] ?? orgName)
                          }
                          render={({ field }) => (
                            <TextField
                              {...field}
                              margin="dense"
                              error={!!errors.companyName}
                              id="companyName"
                              className="inputElement"
                              label="Raison sociale"
                              type="text"
                              fullWidth
                            />
                          )}
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                <Grid item xs={12} md={12}>
                  <Typography variant="body2" sx={{ marginTop: 2, marginBottom: 2 }}>
                    <Link href={googleDriveLink} target="_blank" rel="noopener" underline="none">
                      ➡️ Lien Google Drive
                    </Link>
                  </Typography>
                  <Typography variant="body2">
                    {(depositSaleBuyerData as any)?.client_type === 'pro' || (deal as any)?.BuyerType === 'pro'
                      ? 'KBIS'
                      : "Justificatif de domicile, Passeport ou Carte d'identité (Recto-Verso)"}
                  </Typography>
                  <Button
                    onClick={handleDownloadPhotos}
                    style={{
                      position: 'relative',
                      float: 'right',
                      right: '0px',
                      top: '0px',
                      backgroundColor: theme.palette.saffron.main,
                      zIndex: 1000,
                      cursor: 'pointer',
                    }}
                  >
                    {downloading ? (
                      <CircularProgress size={24} style={{ color: 'white', marginRight: 1 }} />
                    ) : (
                      <SaveIcon color="action" />
                    )}
                  </Button>
                  <CfnPiecesWarning />
                  <FirebaseUploadComponent
                    onBeforeDelete={handleBeforeDelete}
                    onDeleteComplete={handleDeleteComplete}
                    onUploadComplete={handleUploadComplete}
                    uploadedUrls={createCessionCerfaUploadedUrls}
                  />
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button className="secondaryDealActionButton" onClick={handleDialogClose}>
                  Annuler
                </Button>
                <Button
                  className="dealActionButton"
                  variant="outlined"
                  onClick={handleSaveVehicleInfo}
                  disabled={isSavingVehicleInfo}
                  sx={{
                    border: '2px solid #FF6060 !important',
                    color: 'black !important',
                    backgroundColor: 'white !important',
                    '&:hover': {
                      backgroundColor: '#fff5f5 !important',
                      border: '2px solid #FF6060 !important',
                    },
                  }}
                >
                  {isSavingVehicleInfo ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8 }} />
                      Enregistrement...
                    </>
                  ) : (
                    '💾 Enregistrer'
                  )}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmClick}
                  disabled={!isValid || isLoading}
                  className="dealActionButton"
                >
                  {isLoading ? (
                    <>
                      <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                      Génération...
                    </>
                  ) : (
                    '📄 Générer'
                  )}
                </Button>
              </DialogActions>
            </form>
          </FormProvider>
        )}
      </Dialog>
      {/* Modale de confirmation */}
      <Dialog open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
        <DialogContent>
          <Typography>Êtes-vous sûr que toutes les informations sont correctes ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button className="secondaryDealActionButton" onClick={() => setIsConfirmModalOpen(false)}>
            Annuler
          </Button>
          <Button className="dealActionButton" onClick={handleFinalConfirm}>
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorMessageSnackbar
        open={validation.snackbarOpen}
        message={validation.snackbarMessage}
        handleClose={() => validation.setSnackbarOpen(false)}
        severity={validation.severity}
      />
    </>
  );
};

export default CreateCessionCerfa;
